import { apiUrl, PLACE_ID, STYLE_ID } from '@/lib/config'
import type { Journey, Run, RunsResponse, StartRunRequest } from '@/types/journey'
import type { LiveViewConfig, SessionInfo, UsersResponse } from '@/types/liveview'
import type {
  ProximiioFeatureCollection,
  ProximiioFloor,
  ProximiioPlace,
  ProximiioStyleResponse,
} from '@/types/proximi'

/**
 * Thrown for any `401` from the API.
 *
 * There is one reason for it — the session is gone — and one answer, which is
 * the login screen. Callers distinguish it from a genuine failure so a logged
 * out tab does not show "could not load the map" and a Retry button that can
 * never work.
 */
export class UnauthorizedError extends Error {
  constructor() {
    super('not signed in')
    this.name = 'UnauthorizedError'
  }
}

let unauthorizedHandler: (() => void) | null = null

/**
 * Registers the one thing that happens on a `401`, from anywhere.
 *
 * Every call in this module goes through `request`, so the app does not have to
 * remember to handle "signed out" at each call site — the session hook installs
 * a handler here and the login screen comes back on its own.
 */
export function onUnauthorized(handler: (() => void) | null): void {
  unauthorizedHandler = handler
}

/**
 * Every call carries the session cookie and nothing else.
 *
 * `include` rather than the default so a deployment that does put the API on
 * another origin still authenticates; the API's CORS allows exactly the
 * configured origins, with credentials.
 */
async function request(path: string, init?: RequestInit): Promise<Response> {
  const response = await fetch(apiUrl(path), { ...init, credentials: 'include' })
  if (response.status === 401) {
    unauthorizedHandler?.()
    throw new UnauthorizedError()
  }
  return response
}

async function getJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await request(path, init)
  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    throw new Error(`${response.status} ${response.statusText}${detail ? ` — ${detail.slice(0, 200)}` : ''}`)
  }
  return (await response.json()) as T
}

/** Mutations must say they are JSON, and must come from an allowed origin. */
const MUTATION: RequestInit = {
  method: 'POST',
  headers: { 'content-type': 'application/json' },
}

export const getSession = () => getJson<SessionInfo>('/v1/auth/session')

export const login = (email: string, password: string) =>
  getJson<SessionInfo>('/v1/auth/login', { ...MUTATION, body: JSON.stringify({ email, password }) })

/**
 * Trades a platform **user** token for a session — the portal's iframe hands
 * one over as `?token=`, exactly as it does to MapTap. The token goes to the
 * API in a body, once; the page keeps nothing but the cookie that comes back.
 */
export const signInWithToken = (token: string) =>
  getJson<SessionInfo>('/v1/auth/token', { ...MUTATION, body: JSON.stringify({ token }) })

export const logout = () => getJson<{ ok: boolean }>('/v1/auth/logout', { ...MUTATION, body: '{}' })

export const getLiveViewConfig = () => getJson<LiveViewConfig>('/v1/config')

export const getUsers = () => getJson<UsersResponse>('/v1/users')

export const forgetUser = (userId: string) =>
  getJson<{ removed: string }>(`/v1/users/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    headers: { 'content-type': 'application/json' },
  })

export const getStyle = () =>
  getJson<ProximiioStyleResponse>(
    STYLE_ID ? `/v1/proximi/v5/geo/styles/${encodeURIComponent(STYLE_ID)}` : '/v1/proximi/v5/geo/style',
  )

export const getFloors = () =>
  getJson<ProximiioFloor[]>(
    `/v1/proximi/core/floors?skip=0&limit=5000${PLACE_ID ? `&place_id=${encodeURIComponent(PLACE_ID)}` : ''}`,
  )

export const getPlaces = () => getJson<ProximiioPlace[]>('/v1/proximi/core/places?skip=0&limit=500')

const JOURNEY_PAGE_SIZE = 200

/**
 * The organisation's journeys, through the read-only proxy.
 *
 * LiveView cannot write one by construction — the proxy is GET-only and
 * allow-listed — so the editor stays MapTap's and this is a picker. One page is
 * plenty: a venue has a handful of journeys, and the `record-total` header says
 * when it has more than we asked for.
 */
export async function getJourneys(placeId?: string | null): Promise<{
  journeys: Journey[]
  total: number
}> {
  const query = new URLSearchParams({ from: '0', size: String(JOURNEY_PAGE_SIZE) })
  if (placeId) query.set('place_id', placeId)
  const response = await request(`/v1/proximi/v7/geo/journeys?${query}`)
  if (!response.ok) {
    throw new Error(`journeys: ${response.status} ${response.statusText}`)
  }
  const journeys = ((await response.json()) as Journey[]) ?? []
  const total = Number(response.headers.get('record-total') ?? journeys.length)
  return { journeys, total: Number.isFinite(total) ? total : journeys.length }
}

export const getRuns = () => getJson<RunsResponse>('/v1/runs')

export const startRun = (body: StartRunRequest) =>
  getJson<Run>('/v1/runs', { ...MUTATION, body: JSON.stringify(body) })

export const controlRun = (runId: string, action: 'pause' | 'resume' | 'stop') =>
  getJson<Run>(`/v1/runs/${encodeURIComponent(runId)}/${action}`, { ...MUTATION, body: '{}' })

const FEATURE_PAGE_SIZE = 5000

/**
 * Pulls every GeoJSON feature, following the `record-total` header the platform
 * sets on the first page. A venue easily runs to tens of thousands of features,
 * so the pages after the first are fetched concurrently.
 */
export async function getFeatures(): Promise<ProximiioFeatureCollection> {
  const first = await request(`/v1/proximi/v7/geo/features?from=0&size=${FEATURE_PAGE_SIZE}`)
  if (!first.ok) {
    throw new Error(`features: ${first.status} ${first.statusText}`)
  }

  const total = Number(first.headers.get('record-total') ?? '0')
  const page = (await first.json()) as ProximiioFeatureCollection
  const features = [...(page.features ?? [])]

  if (Number.isFinite(total) && total > features.length) {
    const requests: Array<Promise<ProximiioFeatureCollection>> = []
    for (let from = FEATURE_PAGE_SIZE; from < total; from += FEATURE_PAGE_SIZE) {
      requests.push(
        getJson<ProximiioFeatureCollection>(
          `/v1/proximi/v7/geo/features?from=${from}&size=${FEATURE_PAGE_SIZE}`,
        ),
      )
    }
    for (const chunk of await Promise.all(requests)) {
      features.push(...(chunk.features ?? []))
    }
  }

  return { type: 'FeatureCollection', features }
}
